import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { Gift, Copy, Check, Users, Sparkles, Share2, Coins, TrendingUp } from "lucide-react";
import { toast } from "sonner";

export default function Referrals() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { api.get("/referrals/me").then(r => setData(r.data)); }, []);

  if (!data) return <div className="p-8 text-center text-muted-foreground">Indlæser...</div>;

  const link = `${window.location.origin}/register?ref=${data.invite_code}`;

  const copy = (val, msg) => {
    navigator.clipboard.writeText(val);
    setCopied(true);
    toast.success(msg || "Kopieret");
    setTimeout(() => setCopied(false), 2000);
  };

  const share = async () => {
    const text = `Tag jobs og tjen penge på Grabber, brug min invitation og vi får begge 50 kr. når du fuldfører dit første job. ${link}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Grabber, Inviter en ven", text, url: link }); } catch {}
    } else {
      copy(text, "Invitations-tekst kopieret");
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 relative">
      {/* Decorative blobs */}
      <div className="absolute top-0 right-0 w-72 h-72 bg-primary/10 rounded-full blur-3xl -z-10 pointer-events-none animate-float" />
      <div className="absolute top-32 left-0 w-56 h-56 bg-amber-200/40 rounded-full blur-3xl -z-10 pointer-events-none animate-float" style={{ animationDelay: "1.5s" }} />

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
        className="text-center mb-10"
      >
        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-semibold px-3 py-1.5 rounded-full mb-4 uppercase tracking-wider">
          <Gift className="w-3.5 h-3.5" /> Inviter & tjen
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold mb-3">
          Inviter en ven. <br />
          <span className="serif-it text-primary">Få 50 kr., begge.</span>
        </h1>
        <p className="text-muted-foreground max-w-md mx-auto leading-relaxed">
          Del din kode med venner og familie. Når de fuldfører deres første job, får I <span className="font-semibold text-foreground">begge 50 kr.</span> som rabat på næste betaling.
        </p>
      </motion.div>

      {/* Code card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <Card className="relative overflow-hidden border-primary/30 shadow-xl shadow-primary/5 mb-8">
          <div className="absolute -top-12 -right-12 w-40 h-40 bg-primary/15 rounded-full blur-2xl" />
          <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-amber-200/30 rounded-full blur-2xl" />
          <CardContent className="relative py-8 px-6 sm:px-10">
            <p className="text-xs text-muted-foreground uppercase tracking-widest mb-3">Din invitations-kode</p>
            <div className="flex flex-col sm:flex-row gap-3 items-stretch">
              <div className="flex-1 bg-background border-2 border-dashed border-primary/40 rounded-xl px-6 py-4 font-mono text-3xl sm:text-4xl font-bold tracking-[0.25em] text-center text-primary" data-testid="invite-code">
                {data.invite_code}
              </div>
              <Button
                size="lg"
                onClick={() => copy(data.invite_code, "Kode kopieret")}
                className="rounded-xl gap-2 shine-hover"
                data-testid="copy-code-btn"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? "Kopieret!" : "Kopiér"}
              </Button>
            </div>
            <div className="mt-4 flex gap-2">
              <Button variant="outline" size="sm" className="flex-1 gap-2 rounded-lg" onClick={() => copy(link, "Link kopieret")} data-testid="copy-link-btn">
                <Copy className="w-3.5 h-3.5" /> Kopiér link
              </Button>
              <Button variant="outline" size="sm" className="flex-1 gap-2 rounded-lg" onClick={share} data-testid="share-btn">
                <Share2 className="w-3.5 h-3.5" /> Del
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Stats grid */}
      <motion.div
        initial="hidden" animate="show"
        variants={{ show: { transition: { staggerChildren: 0.07 } } }}
        className="grid sm:grid-cols-3 gap-4 mb-8"
      >
        <StatCard icon={<Users />} label="Inviterede" value={data.invited_count} color="bg-blue-50 text-blue-700" />
        <StatCard icon={<Sparkles />} label="Fuldført første job" value={data.completed_count} color="bg-emerald-50 text-emerald-700" />
        <StatCard icon={<Coins />} label="Optjent" value={`${data.total_earned} kr.`} color="bg-amber-50 text-amber-700" />
      </motion.div>

      {/* My credit */}
      {data.credit_balance > 0 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card className="mb-8 bg-gradient-to-br from-primary/5 via-background to-amber-50/30 border-primary/20">
            <CardContent className="py-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center">
                  <Coins className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Din nuværende saldo</p>
                  <p className="text-3xl font-bold text-primary" data-testid="credit-balance">{data.credit_balance.toFixed(2)} kr.</p>
                </div>
              </div>
              <Badge variant="secondary" className="hidden sm:inline-flex">Bruges automatisk ved næste betaling</Badge>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* How it works */}
      <Card className="mb-6">
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Sådan virker det</CardTitle></CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              ["1", "Del din kode", "Send din unikke kode eller link til venner."],
              ["2", "De tilmelder sig", "De opretter en konto med din kode."],
              ["3", "Begge får 50 kr.", "Når de fuldfører deres første job, uanset om de henter eller får hentet, får I begge 50 kr."],
            ].map(([n, t, d]) => (
              <div key={n} className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm shrink-0">{n}</div>
                <div>
                  <p className="font-semibold text-sm mb-1">{t}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Invited list */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Folk jeg har inviteret ({data.invited.length})</CardTitle></CardHeader>
        <CardContent>
          {data.invited.length === 0 ? (
            <div className="text-center py-10">
              <Users className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Ingen invitationer endnu. Del din kode for at komme i gang!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.invited.map((p) => (
                <div key={p.email} className="flex items-center justify-between p-3 rounded-lg border border-border">
                  <div>
                    <p className="font-medium text-sm">{p.full_name || p.email}</p>
                    <p className="text-xs text-muted-foreground">Tilmeldt {new Date(p.created_at).toLocaleDateString("da-DK")}</p>
                  </div>
                  <Badge variant={p.status === "completed" ? "default" : "outline"}>
                    {p.status === "completed" ? "✓ Bonus udløst" : "Afventer første job"}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }}>
      <Card className="p-5 lift">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${color} [&>svg]:w-5 [&>svg]:h-5`}>{icon}</div>
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </Card>
    </motion.div>
  );
}
